import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Save, Send, AlertCircle, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Progress } from '../ui/Progress';

import BasicInfoStep from './steps/BasicInfoStep';
import ProjectStoryStep from './steps/ProjectStoryStep';
import FundingBreakdownStep from './steps/FundingBreakdownStep';
import TeamInfoStep from './steps/TeamInfoStep';
import TimelineRisksStep from './steps/TimelineRisksStep';
import MediaContactStep from './steps/MediaContactStep';
import ReviewSubmitStep from './steps/ReviewSubmitStep';

import projectService from '../../services/projectService';

// Constants
const AUTO_SAVE_DELAY = 30000;

const initialFormState = {
  title: '', funding_for: '', funding_for_other: '', category: '', is_app_listing: false, app_url: '',
  story: '', currency: 'PI', funding_goal: '', campaign_duration: '', country: '', region: '', funding_use: [],
  team_members: [], milestones: [], risks_challenges: '', cover_image: null, gallery_images: [], video_url: null, contact_email: ''
};

const steps = [
  { id: 1, title: 'Basic Information', description: 'Tell us about your project', component: BasicInfoStep, required: ['title', 'funding_for', 'category'] },
  { id: 2, title: 'Project Story', description: 'Share your story and goals', component: ProjectStoryStep, required: ['story', 'funding_goal', 'campaign_duration', 'country'] },
  { id: 3, title: 'Funding Breakdown', description: 'How will you use the funds?', component: FundingBreakdownStep, required: ['funding_use'] },
  { id: 4, title: 'Team Information', description: 'Who\'s working on this project?', component: TeamInfoStep, conditional: true, condition: (formData) => formData.is_app_listing },
  { id: 5, title: 'Timeline & Risks', description: 'Project milestones and challenges', component: TimelineRisksStep, required: [] },
  { id: 6, title: 'Media & Contact', description: 'Images and contact information', component: MediaContactStep, required: ['cover_image', 'contact_email'] },
  { id: 7, title: 'Review & Submit', description: 'Review your project before submission', component: ReviewSubmitStep, required: [] }
];

const ProjectCreationWizard = ({ existingProjectId = null, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [projectId, setProjectId] = useState(existingProjectId);
  const [hasChanges, setHasChanges] = useState(false);

  // Optimized auto-save with useCallback
  const handleAutoSave = useCallback(async () => {
    if (!hasChanges || !projectId || currentStep >= 7) return;
    
    try {
      setIsSaving(true);
      await projectService.saveDraft(formData, projectId);
      setHasChanges(false);
      toast.success('Draft saved automatically', { duration: 2000, icon: '💾' });
    } catch (error) {
      console.warn('Auto-save failed:', error);
    } finally {
      setIsSaving(false);
    }
  }, [formData, hasChanges, projectId, currentStep]);

  // Auto-save effect
  useEffect(() => {
    const timer = setTimeout(handleAutoSave, AUTO_SAVE_DELAY);
    return () => clearTimeout(timer);
  }, [handleAutoSave]);

  // Load existing project (draft, rejected, or other status)
  useEffect(() => {
    if (!existingProjectId) return;
    
    const loadProject = async () => {
      try {
        setIsLoading(true);
        const response = await projectService.getProjectForEdit(existingProjectId);
        if (response.success) {
          // Transform the data to match form structure
          const projectData = response.data;
          
          // Convert Decimal fields to numbers for form
          const transformedData = {
            ...projectData,
            funding_goal: projectData.funding_goal ? parseFloat(projectData.funding_goal.toString()) : '',
            current_funding: projectData.current_funding ? parseFloat(projectData.current_funding.toString()) : 0,
            // Ensure arrays are properly handled
            funding_use: projectData.funding_use || [],
            team_members: projectData.team_members || [],
            gallery_images: projectData.gallery_images || [],
            milestones: projectData.milestones || []
          };
          
          setFormData(transformedData);
          setProjectId(existingProjectId);
        }
      } catch (error) {
        toast.error('Failed to load project: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [existingProjectId]);

  // Memoized visible steps
  const visibleSteps = useMemo(() => 
    steps.filter(step => !step.conditional || step.condition(formData)), [formData]
  );

  const currentStepIndex = visibleSteps.findIndex(step => step.id === currentStep);
  const progress = ((currentStepIndex + 1) / visibleSteps.length) * 100;

  // Update form data
  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
    
    setErrors(prev => {
      const updated = { ...prev };
      Object.keys(updates).forEach(key => delete updated[key]);
      return updated;
    });
  }, []);

  // Validation
  const validateCurrentStep = useCallback(() => {
    const step = steps[currentStep - 1];
    const stepErrors = {};
    
    step.required?.forEach(field => {
      if (!formData[field] || (Array.isArray(formData[field]) && formData[field].length === 0)) {
        stepErrors[field] = 'This field is required';
      }
    });

    if (formData.funding_for === 'others' && !formData.funding_for_other) {
      stepErrors.funding_for_other = 'Please specify what the fund is being raised for';
    }

    if (formData.is_app_listing && !formData.app_url) {
      stepErrors.app_url = 'App URL is required for app listings';
    }

    if (step.id === 3 && formData.funding_use.length > 0) {
      const total = formData.funding_use.reduce((sum, item) => sum + parseFloat(item.percentage || 0), 0);
      if (Math.abs(total - 100) > 0.01) {
        stepErrors.funding_use = 'Percentages must sum to 100%';
      }
    }

    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  }, [currentStep, formData]);

  // Navigation handlers
  const handleNext = async () => {
    if (!validateCurrentStep()) return;

    if (currentStep === 1 && !projectId) {
      try {
        setIsSaving(true);
        const response = await projectService.saveDraft(formData, null);
        if (response.success) {
          setProjectId(response.data.id);
          setHasChanges(false);
          toast.success('Draft created successfully!', { icon: '✨' });
        } else {
          toast.error('Failed to create draft: ' + response.message);
          return;
        }
      } catch (error) {
        toast.error('Failed to create draft: ' + error.message);
        return;
      } finally {
        setIsSaving(false);
      }
    }

    if (currentStep < 7) {
      let nextStepId = currentStep + 1;
      const nextStep = steps[nextStepId - 1];
      
      if (nextStep?.conditional && !nextStep.condition(formData)) {
        nextStepId = nextStepId < 7 ? nextStepId + 1 : 7;
      }
      
      setCurrentStep(nextStepId);
    }
  };

  const handlePrevious = () => {
    for (let i = currentStep - 2; i >= 0; i--) {
      const step = steps[i];
      if (!step.conditional || step.condition(formData)) {
        setCurrentStep(step.id);
        return;
      }
    }
  };

  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      const response = await projectService.saveDraft(formData, projectId);
      
      if (response.success) {
        if (!projectId) setProjectId(response.data.id);
        setHasChanges(false);
        toast.success(response.message, { icon: '💾' });
      }
    } catch (error) {
      toast.error('Failed to save draft: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      
      // Save any pending changes first
      if (hasChanges) await handleSaveDraft();

      // Check if this is a resubmission (project was previously rejected)
      const isResubmission = formData.status === 'rejected';
      
      let response;
      if (isResubmission) {
        // Use resubmit endpoint for rejected projects
        response = await projectService.resubmitProject(projectId, formData);
      } else {
        // Use regular submit for draft projects
        response = await projectService.submitProject(projectId);
      }
      
      if (response.success) {
        toast.success(response.message, { icon: '🎉' });
        onComplete?.(response.data);
      }
    } catch (error) {
      toast.error('Failed to submit project: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const CurrentStepComponent = steps[currentStep - 1]?.component;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center p-8 rounded-2xl bg-white shadow-xl"
        >
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-teal/20 border-t-primary-teal mx-auto mb-6"></div>
          <p className="text-lg text-neutral-600 font-medium">Loading your project...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 px-4 py-6 md:py-12">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Enhanced Progress Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden"
        >
          <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-6 bg-gradient-to-r from-primary-teal/5 to-transparent">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="space-y-1 sm:space-y-2">
                  <CardTitle className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-neutral-900 to-neutral-700 bg-clip-text text-transparent">
                    Create Project
                  </CardTitle>
                  <p className="text-xs sm:text-sm md:text-base text-neutral-600 font-medium">
                    Step {currentStepIndex + 1} of {visibleSteps.length}: {steps[currentStep - 1]?.title}
                  </p>
                </div>
                
                {isSaving && (
                  <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-3 px-4 py-2 bg-primary-teal/10 rounded-full border border-primary-teal/20"
                  >
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-teal/30 border-t-primary-teal"></div>
                    <span className="text-primary-teal font-medium text-sm">Auto-saving...</span>
                  </motion.div>
                )}
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                <Progress value={progress} className="h-2 sm:h-3 bg-neutral-100 rounded-full" />
                
                <div className="flex justify-between items-center text-[10px] sm:text-xs md:text-sm">
                  <span className="text-neutral-500 font-medium line-clamp-1 mr-2">{steps[currentStep - 1]?.description}</span>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-neutral-400 font-bold">{Math.round(progress)}%</span>
                    {progress > 20 && <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />}
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>
        </motion.div>

        {/* Enhanced Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 30, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -30, scale: 0.95 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            {CurrentStepComponent ? (
              <div className="transform">
                <CurrentStepComponent
                  formData={formData}
                  updateFormData={updateFormData}
                  errors={errors}
                  setErrors={setErrors}
                />
              </div>
            ) : (
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8 text-center">
                  <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
                  <h3 className="text-xl font-bold text-neutral-900 mb-3">Step Not Found</h3>
                  <p className="text-neutral-600 mb-6 max-w-md mx-auto">
                    There was an issue loading step {currentStep}. Please try refreshing the page.
                  </p>
                  <Button onClick={() => setCurrentStep(1)} variant="outline" className="px-6">
                    Go to First Step
                  </Button>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Enhanced Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm sticky bottom-4 md:static">
            <CardContent className="p-3 sm:p-4 md:p-6">
              <div className="grid grid-cols-2 md:flex md:flex-row items-center justify-between gap-3 sm:gap-4">
                <div className="flex justify-start">
                  {currentStep > 1 && (
                    <Button
                      variant="outline"
                      onClick={handlePrevious}
                      disabled={isLoading}
                      className="flex items-center gap-2 px-3 sm:px-6 h-11 sm:h-12 border-2 hover:bg-neutral-50 transition-all duration-200 rounded-xl text-sm sm:text-base font-semibold w-full md:w-auto"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Back</span>
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-2 sm:gap-3 md:gap-4 justify-end col-span-2 md:col-span-1 w-full md:w-auto">
                  {currentStep < 7 && (
                    <Button
                      variant="ghost"
                      onClick={handleSaveDraft}
                      disabled={isSaving || !hasChanges}
                      className="flex-1 md:flex-none items-center gap-2 px-3 sm:px-6 h-11 sm:h-12 text-sm sm:text-base font-medium text-neutral-600 hover:text-primary-teal hover:bg-primary-teal/5 transition-all duration-200 rounded-xl"
                    >
                      <Save className="w-4 h-4" />
                      <span className="sm:inline">{isSaving ? '...' : 'Save'}</span>
                    </Button>
                  )}

                  {currentStep < 7 ? (
                    <Button
                      onClick={handleNext}
                      disabled={isLoading || isSaving}
                      className="flex-1 md:flex-none items-center gap-2 px-6 sm:px-8 h-11 sm:h-12 bg-primary-teal text-white shadow-lg hover:shadow-primary-teal/20 transition-all duration-200 rounded-xl font-bold text-sm sm:text-base"
                    >
                      <span>{isSaving && currentStep === 1 ? 'Creating...' : 'Next'}</span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={isLoading || !projectId}
                      className="flex-1 md:flex-none items-center gap-2 px-6 sm:px-8 h-11 sm:h-12 bg-green-600 text-white shadow-lg hover:shadow-green-600/20 transition-all duration-200 rounded-xl font-bold text-sm sm:text-base"
                    >
                      <Send className="w-4 h-4" />
                      <span>{isLoading ? '...' : 'Submit'}</span>
                    </Button>
                  )}
                </div>
              </div>

              {/* Enhanced Error Display */}
              {Object.keys(errors).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-6 p-4 md:p-6 bg-gradient-to-r from-red-50 to-red-50/50 border border-red-200 rounded-xl"
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-red-800 mb-3">Please fix the following errors:</p>
                      <ul className="text-red-700 space-y-2">
                        {Object.entries(errors).map(([field, error]) => (
                          <motion.li 
                            key={field}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="flex items-center gap-2"
                          >
                            <div className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0"></div>
                            <span>{error}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectCreationWizard;
